import { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";
import { normalizeTenantVariableValueDraft } from "@/domains/tenant";
import type {
  TenantVariableValueDraft,
  TenantVariableView,
} from "@/domains/tenant";
import type { Json } from "@/types/supabase.types";

type Option = {
  label: string;
  value: string;
};

type TenantVariableValueFieldProps = {
  id?: string;
  isSaving?: boolean;
  label?: string;
  showLabel?: boolean;
  variable: TenantVariableView;
  onSave: (value: TenantVariableValueDraft) => void;
};

const TEXT_SAVE_DELAY_MS = 500;

function optionLabelFromValue(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function parseOptions(raw: Json): Option[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (typeof item === "string") {
      return [{ value: item, label: optionLabelFromValue(item) }];
    }
    if (item && typeof item === "object" && !Array.isArray(item)) {
      const record = item as Record<string, unknown>;
      const rawValue = record.value ?? record.key ?? record.id;
      if (typeof rawValue !== "string") return [];
      const rawLabel = record.label ?? record.name ?? record.title;
      return [
        {
          value: rawValue,
          label:
            typeof rawLabel === "string"
              ? rawLabel
              : optionLabelFromValue(rawValue),
        },
      ];
    }
    return [];
  });
}

function jsonItemToText(value: Json): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (value === null) return "null";
  return JSON.stringify(value);
}

function valueToText(value: Json | null): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) return value.map(jsonItemToText).join(", ");
  return JSON.stringify(value, null, 2);
}

export function TenantVariableValueField({
  id,
  isSaving = false,
  label: labelProp,
  showLabel = true,
  variable,
  onSave,
}: TenantVariableValueFieldProps) {
  const [textValue, setTextValue] = useState(() =>
    valueToText(variable.current_value),
  );
  const [error, setError] = useState<string | null>(null);
  const textSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTextFieldFocusedRef = useRef(false);
  const options = useMemo(
    () => parseOptions(variable.variable_options),
    [variable.variable_options],
  );

  useEffect(() => {
    if (!isTextFieldFocusedRef.current) {
      setTextValue(valueToText(variable.current_value));
    }
  }, [variable.current_value, variable.variable_id]);

  useEffect(() => {
    return () => {
      if (textSaveTimeoutRef.current) {
        clearTimeout(textSaveTimeoutRef.current);
      }
    };
  }, []);

  const label =
    labelProp ?? variable.variable_name_frontend ?? variable.variable_name;
  const fieldId = id ?? `tenant-variable-value-${variable.variable_id}`;
  const isTextarea =
    variable.variable_type === "text" || variable.variable_type === "json";
  const isNumeric =
    variable.variable_type === "number" || variable.variable_type === "integer";
  const isBoolean = variable.variable_type === "boolean";
  const isSelect =
    options.length > 0 &&
    (variable.variable_type === "enum" || variable.variable_type === "array");

  const saveValue = (rawValue: string | boolean) => {
    setError(null);
    try {
      onSave(normalizeTenantVariableValueDraft({ variable, value: rawValue }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Invalid value");
    }
  };

  const handleTextChange = (value: string) => {
    setTextValue(value);
    if (textSaveTimeoutRef.current) {
      clearTimeout(textSaveTimeoutRef.current);
    }
    textSaveTimeoutRef.current = setTimeout(() => {
      saveValue(value);
      textSaveTimeoutRef.current = null;
    }, TEXT_SAVE_DELAY_MS);
  };

  const handleTextBlur = () => {
    isTextFieldFocusedRef.current = false;
    if (textSaveTimeoutRef.current) {
      clearTimeout(textSaveTimeoutRef.current);
      textSaveTimeoutRef.current = null;
      saveValue(textValue);
    }
  };

  const handleTextFocus = () => {
    isTextFieldFocusedRef.current = true;
  };

  const handleBooleanChange = (checked: boolean) => {
    saveValue(checked);
  };

  return (
    <div className="grid gap-2">
      {showLabel && (
        <label
          htmlFor={fieldId}
          className="flex items-center gap-2 text-sm leading-none font-medium select-none"
        >
          {label}
        </label>
      )}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {isBoolean ? (
          <label className="flex h-9 items-center gap-2 text-sm">
            <Checkbox
              id={fieldId}
              checked={variable.current_value === true}
              onCheckedChange={(checked) =>
                handleBooleanChange(checked === true)
              }
              disabled={isSaving}
            />
            {showLabel && "Enabled"}
          </label>
        ) : isSelect ? (
          <Select
            value={textValue}
            onValueChange={handleTextChange}
            disabled={isSaving}
          >
            <SelectTrigger
              aria-label={showLabel ? undefined : label}
              id={fieldId}
              className="w-full sm:min-w-52"
            >
              <SelectValue placeholder="Select value" />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : isTextarea ? (
          <Textarea
            aria-label={showLabel ? undefined : label}
            id={fieldId}
            value={textValue}
            onChange={(event) => handleTextChange(event.target.value)}
            onBlur={handleTextBlur}
            onFocus={handleTextFocus}
            disabled={isSaving}
            className="min-h-20 font-mono text-xs"
          />
        ) : (
          <Input
            aria-label={showLabel ? undefined : label}
            id={fieldId}
            type={
              isNumeric
                ? "number"
                : variable.variable_type === "date"
                  ? "date"
                  : "text"
            }
            value={textValue}
            onChange={(event) => handleTextChange(event.target.value)}
            onBlur={handleTextBlur}
            onFocus={handleTextFocus}
            disabled={isSaving}
            className="w-full"
          />
        )}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
